import { Box, CircularProgress, Theme, createStyles, makeStyles } from "@material-ui/core"
import React from "react"
import ErrorList from "../../components/ErrorList"
import DateRangeSelector from "../../hour-reports/components/DateRangeSelector/DateRangeSelector"
import { formatDateRangeQuery, useDateRange } from "../../hour-reports/components/DateRangeSelector/use-date-range"
import { useGetReportedHoursQuery } from "../../hour-reports/api"
import { useHourReportingWorkflowSettings } from "../../hour-reports/hooks/use-hour-reporting-workflow-settings"
import { useProjectCalendarSettings } from "../../routes/providers/project-calendar-settings"
import ReportedHoursTable from "../../treegrid/reported-hours"
import type { ProjectViewModel } from "../api/project"
import { reportedHoursGridConfigs } from "../../treegrid/reported-hours/column-settings"

const ProjectReportedHours = ({ project }: ProjectReportedHoursProps) => {
  const classes = useStyles()
  const { calendarSettings } = useProjectCalendarSettings()
  const { fromDate, toDate, setFromDate, setToDate, setDateRange } = useDateRange()
  const orgId = project.isOrgProject ? project.maintainerId : ""
  const workflowSettingsQuery = useHourReportingWorkflowSettings(orgId)
  const queryParams = React.useMemo(
    () => ({
      projectId: project.id,
      fromDate: formatDateRangeQuery(fromDate),
      toDate: formatDateRangeQuery(toDate),
    }),
    [fromDate, project.id, toDate]
  )
  const reportedHoursQuery = useGetReportedHoursQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  })

  if (reportedHoursQuery.isError || workflowSettingsQuery.isError) {
    return <ErrorList errors={["Failed to fetch reported hours"]} />
  }

  const isLoading = reportedHoursQuery.isLoading || workflowSettingsQuery.isLoading
  const reports = reportedHoursQuery.currentData ?? []

  return (
    <Box className={classes.container}>
      <Box mb={2}>
        <DateRangeSelector
          fromDate={fromDate}
          toDate={toDate}
          firstDayOfWeek={calendarSettings.firstDayOfWeek}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onDateRangeChange={setDateRange}
        />
      </Box>

      <Box className={classes.tableContainer}>
        {isLoading ? (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <ReportedHoursTable
            reports={reports}
            dateFormat={calendarSettings.dateFnsDateFormat}
            dateSeparator={calendarSettings.dateFormatSpecs.dateSeparator}
            firstDayOfWeek={calendarSettings.firstDayOfWeek}
            gridInfo={project.fullTitle}
            workflowSettings={workflowSettingsQuery.settings}
            gridConfig={
              project.isOrgProject ? reportedHoursGridConfigs.organisationResource : reportedHoursGridConfigs.personal
            }
          />
        )}
      </Box>
    </Box>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: "100%",
      maxWidth: theme.breakpoints.values.lg,
      margin: "0 auto",
    },
    tableContainer: {
      position: "relative",
    },
    loadingOverlay: {
      minHeight: 360,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.palette.background.paper,
    },
  })
)

type ProjectReportedHoursProps = {
  project: ProjectViewModel
}

export default ProjectReportedHours
